import { useSubscription } from '@apollo/client';
import { useMemo, useState } from 'react';

import { formatErrorForLogging } from '@eluve/eluve-errors';
import { graphql } from '@eluve/graphql.tada';
import { useNamedLogger } from '@eluve/logger';
import { useDb } from '@eluve/user-local-files';

import { useAppointmentContext } from './appointment.context';

const pendingAudioFilesWithDevicesFragment = graphql(`
  fragment PendingAudioFilesWithDevices on Appointments @_unmask {
    __typename
    tenantId
    id
    appointmentFiles(
      where: {
        fileType: { _in: [USER_BACKUP_AUDIO, CHUNKED_AUDIO] }
        userUpload: { status: { _eq: PENDING_UPLOAD } }
      }
    ) {
      __typename
      tenantId
      appointmentId
      fileId
      userUpload {
        __typename
        tenantId
        id
        originalFileName
        transcript {
          __typename
          appointment_segment {
            __typename
            tenantId
            appointmentId
            id
            device {
              __typename
              tenantId
              appointmentId
              segmentId
              deviceType
              browserName
              operatingSystem
              model
            }
          }
        }
      }
    }
  }
`);

const pendingUploadDevicesSubscription = graphql(
  `
    subscription pendingUploadFilesSubscription(
      $tenantId: uuid!
      $appointmentId: uuid!
    ) {
      appointmentsByPk(tenantId: $tenantId, id: $appointmentId) {
        ...PendingAudioFilesWithDevices
      }
    }
  `,
  [pendingAudioFilesWithDevicesFragment],
);

export interface PendingDeviceInfo {
  browserName: string;
  operatingSystem: string;
  deviceType: string;
  model: string | null;
}

/**
 * Hook to subscribe to pending audio files from the server and determine which files
 * are being uploaded from other devices/browsers (not available locally).
 *
 * @returns Array of device info for files being uploaded from other devices.
 */
export const usePendingUploadDevices = (): PendingDeviceInfo[] => {
  const [localFileNames, setLocalFileNames] = useState<Set<string>>(new Set());

  const { tenantId, appointmentId } = useAppointmentContext();
  const db = useDb();
  const logger = useNamedLogger('usePendingUploadDevices');

  const { data } = useSubscription(pendingUploadDevicesSubscription, {
    variables: { tenantId, appointmentId },
    skip: !tenantId || !appointmentId,
    onData: () => {
      db.appointmentFiles
        .where('appointmentId')
        .equals(appointmentId)
        .toArray()
        .then((files) =>
          setLocalFileNames(new Set(files.map((file) => file.id))),
        )
        .catch((error) => {
          logger.warn('Failed to query local appointment files', {
            appointmentId,
            tenantId,
            error: formatErrorForLogging(error),
          });
        });
    },
  });

  const pendingDevices = useMemo(() => {
    const appointment = data?.appointmentsByPk;
    if (!appointment) {
      return [];
    }

    const pendingFiles = appointment.appointmentFiles ?? [];
    const deviceInfoMap = new Map<string, PendingDeviceInfo>();

    for (const file of pendingFiles) {
      const originalFileName = file.userUpload?.originalFileName;
      if (!originalFileName || localFileNames.has(originalFileName)) {
        continue;
      }

      const device = file.userUpload?.transcript?.appointment_segment?.device;
      if (!device) {
        continue;
      }

      const { browserName, operatingSystem, deviceType } = device;
      if (!browserName || !operatingSystem || !deviceType) {
        continue;
      }

      const key = `${browserName}-${operatingSystem}`;
      if (!deviceInfoMap.has(key)) {
        deviceInfoMap.set(key, {
          browserName,
          operatingSystem,
          deviceType,
          model: device.model || null,
        });
      }
    }

    return Array.from(deviceInfoMap.values());
  }, [data, localFileNames]);

  return pendingDevices;
};
